import { FilterTable } from "@/features/FilterTable/FilterTable";
import { Field } from "@/features/FilterTable/types";
import { DepField, makeFiltersBuilder } from "@/features/FilterTable/utils";
import { preprocessTextAsyncAutocompleteField } from "@/features/FormCard/utils";
import { createCurrencyOption } from "@/hooks/useCurrencyOption";
import { Clients } from "@/lib/clients";
import { Tables } from "@/gen/tableNames/tables";
import { countries_options } from "@/components/Form/CountryPhone/data";
import { z } from "zod";
import { useTranslation } from "react-i18next";

const TABLE_NAME = Tables.S3_PORTFOLIOS;

const filterS3PortfoliosSchema = z.object({
  client_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  vehicle_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  account_name_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  name_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  currency_contains: z.string().optional(),
  reporting_currency_contains: z.string().optional(),
  bank_name_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  bank_reporting_currency_contains: z.string().optional(),
  bank_country_contains: z.string().optional(),
  account_type_contains: z.string().optional(),
  investment_type_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  contract_name_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  code_contains: z
    .preprocess(preprocessTextAsyncAutocompleteField, z.string())
    .optional()
    .nullable(),
  collateral_contains: z.string().optional(),
});

export type FilterS3PortfoliosSchema = z.infer<typeof filterS3PortfoliosSchema>;

const portfolioDepFields: readonly DepField<FilterS3PortfoliosSchema>[] = [
  {
    name: "bank_name_contains",
    key: "bank_name",
    extract: "text",
    bucket: "equals",
  },
  {
    name: "client_contains",
    key: "client",
    extract: "text",
    bucket: "equals",
  },
  {
    name: "vehicle_contains",
    key: "vehicle",
    extract: "text",
    bucket: "equals",
  },
  {
    name: "investment_type_contains",
    key: "investment_type",
    extract: "text",
    bucket: "equals",
  },
  {
    name: "contract_name_contains",
    key: "contract_name",
    extract: "text",
    bucket: "equals",
  },
  {
    name: "name_contains",
    key: "id",
    extract: "id",
    bucket: "equals",
  },
  {
    name: "code_contains",
    key: "code",
    extract: "text",
    bucket: "equals",
  },
  {
    name: "account_name_contains",
    key: "account_name",
    extract: "text",
    bucket: "equals",
  },
];

export function FilterS3Portfolios() {
  const { t, i18n } = useTranslation("tables");

  const fields: Field[] = [
    {
      name: "client_contains",
      label: t(`${TABLE_NAME}.searchbox.client_contains`, { defaultValue: "Client" }),
      type: "asyncAutocomplete",
      base_url: `${Clients.S3}/${Tables.S3_PORTFOLIOS}`,
      column_name: "client",
      filters: makeFiltersBuilder<FilterS3PortfoliosSchema>(
        portfolioDepFields,
        "client_contains",
      ),
    },
    {
      name: "vehicle_contains",
      label: t(`${TABLE_NAME}.searchbox.vehicle_contains`, { defaultValue: "Vehicle / Beneficiary" }),
      type: "asyncAutocomplete",
      base_url: `${Clients.S3}/${Tables.S3_PORTFOLIOS}`,
      column_name: "vehicle",
      filters: makeFiltersBuilder<FilterS3PortfoliosSchema>(
        portfolioDepFields,
        "vehicle_contains",
      ),
    },
    {
      name: "account_name_contains",
      label: t(`${TABLE_NAME}.searchbox.account_name_contains`, { defaultValue: "Account group" }),
      type: "asyncAutocomplete",
      base_url: `${Clients.S3}/${Tables.S3_PORTFOLIOS}`,
      column_name: "account_name",
      filters: makeFiltersBuilder<FilterS3PortfoliosSchema>(
        portfolioDepFields,
        "account_name_contains",
      ),
    },
    {
      name: "name_contains",
      label: t(`${TABLE_NAME}.searchbox.name_contains`, { defaultValue: "Account" }),
      type: "asyncAutocomplete",
      base_url: `${Clients.S3}/${Tables.S3_PORTFOLIOS}`,
      column_name: "name",
      filters: makeFiltersBuilder<FilterS3PortfoliosSchema>(
        portfolioDepFields,
        "name_contains",
      ),
    },
    {
      name: "currency_contains",
      label: t(`${TABLE_NAME}.searchbox.currency_contains`, { defaultValue: "Portfolio currency" }),
      type: "autocomplete",
      options: createCurrencyOption(),
    },
    {
      name: "reporting_currency_contains",
      label: t(`${TABLE_NAME}.searchbox.reporting_currency_contains`, { defaultValue: "Reporting currency" }),
      type: "autocomplete",
      options: createCurrencyOption(),
    },
    {
      name: "bank_name_contains",
      label: t(`${TABLE_NAME}.searchbox.bank_name_contains`, { defaultValue: "Bank" }),
      type: "asyncAutocomplete",
      base_url: `${Clients.S3}/${Tables.S3_PORTFOLIOS}`,
      column_name: "bank_name",
      filters: makeFiltersBuilder<FilterS3PortfoliosSchema>(
        portfolioDepFields,
        "bank_name_contains",
      ),
    },
    {
      name: "account_type_contains",
      label: t(`${TABLE_NAME}.searchbox.account_type_contains`, { defaultValue: "Account type" }),
      type: "select",
      useTranslation: true,
      client: Clients.S3,
      table_name: Tables.S3_PORTFOLIOS,
      language: i18n.resolvedLanguage?.startsWith("fr") ? "fr" : "en",
      translate_key: "account_type",
    },
    {
      name: "bank_reporting_currency_contains",
      label: t(`${TABLE_NAME}.searchbox.bank_reporting_currency_contains`, { defaultValue: "Management currency" }),
      type: "autocomplete",
      options: createCurrencyOption(),
    },
    {
      name: "bank_country_contains",
      label: t(`${TABLE_NAME}.searchbox.bank_country_contains`, { defaultValue: "Bank country" }),
      type: "autocomplete",
      options: countries_options,
    },
    {
      name: "investment_type_contains",
      label: t(`${TABLE_NAME}.searchbox.investment_type_contains`, { defaultValue: "Investment type" }),
      type: "asyncAutocomplete",
      base_url: `${Clients.S3}/${Tables.S3_PORTFOLIOS}`,
      column_name: "investment_type",
      filters: makeFiltersBuilder<FilterS3PortfoliosSchema>(
        portfolioDepFields,
        "investment_type_contains",
      ),
    },
    {
      name: "contract_name_contains",
      label: t(`${TABLE_NAME}.searchbox.contract_name_contains`, { defaultValue: "Contract name" }),
      type: "asyncAutocomplete",
      base_url: `${Clients.S3}/${Tables.S3_PORTFOLIOS}`,
      column_name: "contract_name",
      filters: makeFiltersBuilder<FilterS3PortfoliosSchema>(
        portfolioDepFields,
        "contract_name_contains",
      ),
    },
    {
      name: "code_contains",
      label: t(`${TABLE_NAME}.searchbox.code_contains`, { defaultValue: "Code" }),
      type: "asyncAutocomplete",
      base_url: `${Clients.S3}/${Tables.S3_PORTFOLIOS}`,
      column_name: "code",
      filters: makeFiltersBuilder<FilterS3PortfoliosSchema>(
        portfolioDepFields,
        "code_contains",
      ),
    },
    {
      name: "collateral_contains",
      label: t(`${TABLE_NAME}.searchbox.collateral_contains`, { defaultValue: "Pledged securities account" }),
      type: "select",
      useTranslation: false,
      options: [
        { value: "true", label: "Oui" },
        { value: "false", label: "Non" },
      ],
    },
  ];

  return (
    <FilterTable
      client={Clients.S3}
      schema={filterS3PortfoliosSchema}
      tableName={Tables.S3_PORTFOLIOS}
      fields={fields}
      columns={2}
    />
  );
}
